import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { PATIENT_RECORD_STATUS, type PatientRecord, type PatientRecordStatus } from 'types/patient';
import { formatUnifiedForDisplay } from 'utils/date';

const EMPTY_VALUE = '—';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const getSickLeaveStartDate = (record: PatientRecord): string | null => {
  const fromValues = record.values?.fecha_baja;
  if (isNonEmptyString(fromValues)) return fromValues;

  const exactField = record.fields?.find(field => field.key === 'fecha_baja');
  if (isNonEmptyString(exactField?.value)) return exactField.value;

  const fuzzyField = record.fields?.find(field => {
    const key = field.key?.toLowerCase() ?? '';
    const label = field.label?.toLowerCase() ?? '';
    return key.includes('baja') || label.includes('baja');
  });

  if (isNonEmptyString(fuzzyField?.value)) return fuzzyField.value;

  return null;
};

export const formatRecordDate = (value: string | null | undefined): string => {
  if (!value) return EMPTY_VALUE;
  try {
    return formatUnifiedForDisplay(value, 'dd/MM/yyyy');
  } catch {
    return value;
  }
};

export const getRecordStatusLabel = (status: PatientRecordStatus): string => {
  if (status === PATIENT_RECORD_STATUS.OPEN) {
    return t('PatientRecords.status.open', { ns: LOCIZE_NS.INSURANCE });
  }
  if (status === PATIENT_RECORD_STATUS.CLOSED) {
    return t('PatientRecords.status.closed', { ns: LOCIZE_NS.INSURANCE });
  }
  return t('PatientRecords.status.cancelled', { ns: LOCIZE_NS.INSURANCE });
};

export const getRecordStatusClassName = (status: PatientRecordStatus): string => {
  if (status === PATIENT_RECORD_STATUS.OPEN) return 'text-teal-600';
  if (status === PATIENT_RECORD_STATUS.CLOSED) return 'text-red-500';
  return 'text-gray-500';
};

export const getRecordEndDate = (record: PatientRecord): string => {
  if (record.status === PATIENT_RECORD_STATUS.OPEN) return EMPTY_VALUE;
  return formatRecordDate(record.closedAt);
};

export const getRecordContractCode = (record: PatientRecord): string =>
  record.contract?.code || EMPTY_VALUE;
